import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Plus, Trash2, Edit3, Save, Loader2, Languages, X, LogOut, Eye } from "lucide-react";
import AdminTabs from "@/components/AdminTabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "artlenss-admin-token";

const emptyForm = {
  id: null,
  slug: "",
  title_ar: "",
  title_en: "",
  excerpt_ar: "",
  excerpt_en: "",
  body_ar: "",
  body_en: "",
  cover_image: "",
  author: "Hani Hijazi",
  published: true,
};

const AdminBlog = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [articles, setArticles] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [showEditor, setShowEditor] = useState(false);
  const [translating, setTranslating] = useState({ title: false, excerpt: false, body: false });

  const authHeaders = { Authorization: `Bearer ${token}` };

  const loadArticles = async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/admin/blog/articles`, { headers: authHeaders });
      setArticles(data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error("انتهت الجلسة، أعد تسجيل الدخول");
      }
    }
  };

  useEffect(() => { loadArticles(); /* eslint-disable-next-line */ }, [token]);

  const doLogin = async () => {
    if (!password.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
      toast.success("مرحباً بك في لوحة التحكم 🎬");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "كلمة المرور غير صحيحة");
    } finally {
      setLoading(false);
    }
  };

  const doLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setArticles([]);
    toast("تم تسجيل الخروج");
  };

  const openEditor = (article = null) => {
    if (article) setForm({ ...article });
    else setForm(emptyForm);
    setShowEditor(true);
  };

  const closeEditor = () => {
    setShowEditor(false);
    setForm(emptyForm);
  };

  const translate = async (field) => {
    const arField = `${field}_ar`;
    const enField = `${field}_en`;
    if (!form[arField]?.trim()) {
      toast.error("اكتب النص العربي أولاً");
      return;
    }
    setTranslating((s) => ({ ...s, [field]: true }));
    try {
      const { data } = await axios.post(
        `${API}/admin/blog/translate`,
        { text: form[arField], target: "en" },
        { headers: authHeaders }
      );
      setForm((f) => ({ ...f, [enField]: data.translated }));
      toast.success("تمت الترجمة ✨");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشلت الترجمة");
    } finally {
      setTranslating((s) => ({ ...s, [field]: false }));
    }
  };

  const saveArticle = async () => {
    if (!form.title_ar && !form.title_en) {
      toast.error("العنوان مطلوب (عربي أو إنجليزي)");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.id) delete payload.id;
      await axios.post(`${API}/admin/blog/articles`, payload, { headers: authHeaders });
      toast.success(form.id ? "تم التحديث" : "تم النشر");
      closeEditor();
      await loadArticles();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل الحفظ");
    } finally {
      setLoading(false);
    }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm("هل تريد حذف هذا المقال نهائياً؟")) return;
    try {
      await axios.delete(`${API}/admin/blog/articles/${id}`, { headers: authHeaders });
      toast.success("تم الحذف");
      await loadArticles();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل الحذف");
    }
  };

  // -------- LOGIN VIEW --------
  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0A0C] text-white flex items-center justify-center p-4" data-testid="admin-login">
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0d0d10] p-8 md:p-10">
          <div className="w-14 h-14 rounded-full bg-[#E6A95B] text-[#0A0A0C] grid place-items-center mx-auto mb-6">
            <Lock className="w-6 h-6" />
          </div>
          <h1 className="font-display text-3xl text-center mb-2">لوحة تحكم المدونة</h1>
          <p className="text-white/50 text-center text-sm mb-8">ARTLENSS Blog Admin</p>
          <label className="block text-xs uppercase tracking-widest text-[#E6A95B] mb-2">كلمة المرور</label>
          <input
            data-testid="admin-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doLogin()}
            className="w-full bg-black/40 border border-white/15 rounded-lg px-4 py-3 focus:outline-none focus:border-[#E6A95B]"
            autoFocus
          />
          <button
            data-testid="admin-login-btn"
            onClick={doLogin}
            disabled={loading}
            className="mt-6 w-full py-3 rounded-full bg-[#E6A95B] text-[#0A0A0C] font-bold hover:brightness-110 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            دخول
          </button>
        </div>
      </div>
    );
  }

  // -------- ADMIN DASHBOARD --------
  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 px-6 py-5">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
          <div>
            <div className="text-[#E6A95B] text-xs uppercase tracking-widest">ARTLENSS · Admin</div>
            <div className="font-display text-2xl">إدارة المدونة</div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/blog" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-[#E6A95B] inline-flex items-center gap-2 text-sm">
              <Eye className="w-4 h-4" /> معاينة
            </a>
            <button
              onClick={() => openEditor()}
              data-testid="new-article-btn"
              className="px-4 py-2 rounded-full bg-[#E6A95B] text-[#0A0A0C] font-bold text-sm inline-flex items-center gap-2 hover:brightness-110"
            >
              <Plus className="w-4 h-4" /> مقال جديد
            </button>
            <button onClick={doLogout} className="text-white/60 hover:text-white p-2" title="خروج">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
        <AdminTabs />
      </header>

      <main className="p-6 md:p-10 max-w-6xl mx-auto">
        {articles.length === 0 ? (
          <div className="border border-dashed border-white/10 rounded-2xl p-16 text-center text-white/60">
            لا توجد مقالات بعد — اضغط "مقال جديد" لإنشاء أول مقال.
          </div>
        ) : (
          <div className="space-y-4">
            {articles.map((a) => (
              <div key={a.id} className="flex items-center gap-4 p-4 rounded-xl border border-white/10 bg-[#0d0d10]" data-testid={`article-row-${a.slug}`}>
                <div className="w-16 h-16 shrink-0 rounded-lg overflow-hidden bg-black/40 grid place-items-center">
                  {a.cover_image ? <img src={a.cover_image} alt="" className="w-full h-full object-cover" /> : <Edit3 className="w-6 h-6 text-white/30" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg truncate">{a.title_ar || a.title_en || "بدون عنوان"}</div>
                  <div className="text-white/50 text-xs mt-1">
                    /{a.slug} · {a.published ? "منشور" : "مسودة"} · {new Date(a.created_at).toLocaleDateString("ar-SA")}
                  </div>
                </div>
                <button onClick={() => openEditor(a)} className="p-2 text-white/70 hover:text-[#E6A95B]" title="تعديل">
                  <Edit3 className="w-4 h-4" />
                </button>
                <button onClick={() => deleteArticle(a.id)} className="p-2 text-white/70 hover:text-red-400" title="حذف">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* EDITOR MODAL */}
      {showEditor && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur overflow-y-auto" data-testid="editor-modal">
          <div className="max-w-4xl mx-auto my-10 rounded-2xl border border-white/10 bg-[#0d0d10] p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl">{form.id ? "تعديل مقال" : "مقال جديد"}</h2>
              <button onClick={closeEditor} className="p-2 text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <Field label="رابط المقال (Slug — يُنشأ تلقائياً إن تُرك فارغاً)">
              <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="my-article-slug" className="input-admin" />
            </Field>

            <Field label="صورة الغلاف (URL)">
              <input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} placeholder="/covers/..." className="input-admin" />
            </Field>

            <BiField
              label="العنوان"
              arValue={form.title_ar}
              enValue={form.title_en}
              onAr={(v) => setForm({ ...form, title_ar: v })}
              onEn={(v) => setForm({ ...form, title_en: v })}
              onTranslate={() => translate("title")}
              translating={translating.title}
              multiline={false}
            />

            <BiField
              label="مقتطف (Excerpt)"
              arValue={form.excerpt_ar}
              enValue={form.excerpt_en}
              onAr={(v) => setForm({ ...form, excerpt_ar: v })}
              onEn={(v) => setForm({ ...form, excerpt_en: v })}
              onTranslate={() => translate("excerpt")}
              translating={translating.excerpt}
              multiline
              rows={3}
            />

            <BiField
              label="المحتوى (Body)"
              arValue={form.body_ar}
              enValue={form.body_en}
              onAr={(v) => setForm({ ...form, body_ar: v })}
              onEn={(v) => setForm({ ...form, body_en: v })}
              onTranslate={() => translate("body")}
              translating={translating.body}
              multiline
              rows={12}
            />

            <label className="flex items-center gap-2 mt-4 text-sm text-white/70">
              <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
              نشر المقال (اجعله مرئياً في /blog)
            </label>

            <div className="mt-8 flex items-center justify-end gap-3">
              <button onClick={closeEditor} className="px-5 py-2.5 rounded-full border border-white/20 text-white/70">إلغاء</button>
              <button
                onClick={saveArticle}
                disabled={loading}
                data-testid="save-article-btn"
                className="px-5 py-2.5 rounded-full bg-[#E6A95B] text-[#0A0A0C] font-bold inline-flex items-center gap-2 disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {form.id ? "تحديث" : "نشر"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .input-admin { width:100%; background: rgba(0,0,0,0.4); border:1px solid rgba(255,255,255,0.15); border-radius:10px; padding:12px 14px; color:#fff; outline:none; }
        .input-admin:focus { border-color:#E6A95B; }
        .textarea-admin { width:100%; background: rgba(0,0,0,0.4); border:1px solid rgba(255,255,255,0.15); border-radius:10px; padding:12px 14px; color:#fff; outline:none; resize:vertical; font-family: inherit; line-height:1.7; }
        .textarea-admin:focus { border-color:#E6A95B; }
      `}</style>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div className="mb-4">
    <label className="block text-xs uppercase tracking-widest text-[#E6A95B] mb-2">{label}</label>
    {children}
  </div>
);

const BiField = ({ label, arValue, enValue, onAr, onEn, onTranslate, translating, multiline, rows = 3 }) => (
  <div className="mb-5 grid grid-cols-1 md:grid-cols-2 gap-4">
    <div>
      <label className="block text-xs uppercase tracking-widest text-[#E6A95B] mb-2">{label} · العربية</label>
      {multiline ? (
        <textarea rows={rows} value={arValue} onChange={(e) => onAr(e.target.value)} dir="rtl" className="textarea-admin" />
      ) : (
        <input value={arValue} onChange={(e) => onAr(e.target.value)} dir="rtl" className="input-admin" />
      )}
    </div>
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs uppercase tracking-widest text-[#E6A95B]">{label} · English</label>
        <button
          type="button"
          onClick={onTranslate}
          disabled={translating}
          data-testid={`translate-${label.includes("العنوان") ? "title" : label.includes("مقتطف") ? "excerpt" : "body"}-btn`}
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-[#E6A95B]/50 text-[#E6A95B] text-xs hover:bg-[#E6A95B] hover:text-[#0A0A0C] transition disabled:opacity-50"
        >
          {translating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />}
          ترجمة تلقائية
        </button>
      </div>
      {multiline ? (
        <textarea rows={rows} value={enValue} onChange={(e) => onEn(e.target.value)} dir="ltr" className="textarea-admin" />
      ) : (
        <input value={enValue} onChange={(e) => onEn(e.target.value)} dir="ltr" className="input-admin" />
      )}
    </div>
  </div>
);

export default AdminBlog;
